import { Card, CardTitle } from "@/components/Card";
import type { ComputedFieldDocument } from "@/lib/types";
import { RowField, RowListField, RowTextInput } from "@/components/strategy/FieldEditors";
import { usePhaseEditor, type PhaseEditorProps } from "@/components/strategy/phaseEditing";

const KEY_PATTERN = /^[a-z0-9_]{1,40}$/;

/**
 * Values derived per prospect when this phase writes an email, available to
 * scripts and examples as `{{computed.<key>}}` and shown to the agent as
 * precomputed context. Evaluated in order, so a later field may use an
 * earlier one as `computed.<key>`.
 */
export default function ComputedFieldsCard(props: PhaseEditorProps) {
  const { list, edit, isEdited } = usePhaseEditor(props);
  const fields = list<ComputedFieldDocument>(["computed_fields"]);

  return (
    <Card>
      <div className="flex items-center gap-1.5">
        <CardTitle>Computed fields</CardTitle>
        {isEdited(["computed_fields"]) ? (
          <span title="unsaved change" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        ) : null}
      </div>
      <p className="mb-2 text-xs text-[var(--fg-muted)]">
        Calculated per prospect from its fields. Use in scripts as{" "}
        <code>{"{{computed.<key>}}"}</code>; the agent also sees the values. Expressions
        support arithmetic, comparisons, <code>&amp;&amp;</code> / <code>||</code> /{" "}
        <code>!</code>, string joining with <code>+</code>, and the functions{" "}
        <code>round</code>, <code>default</code>, <code>concat</code>, <code>upper</code>,{" "}
        <code>lower</code>, <code>trim</code>, <code>length</code>. Read prospect data as{" "}
        <code>prospect.custom_fields.&lt;key&gt;</code> and earlier fields as{" "}
        <code>computed.&lt;key&gt;</code>.
      </p>
      <RowListField<ComputedFieldDocument>
        value={fields}
        rowLabel="computed field"
        addLabel="Add computed field"
        newRow={() => ({ key: "", expression: "" })}
        onCommit={(rows) => edit(["computed_fields"], rows)}
        renderRow={(row, update, i) => {
          const badKey = typeof row.key === "string" && row.key !== "" && !KEY_PATTERN.test(row.key);
          return (
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <RowField label="key">
                <RowTextInput
                  ariaLabel={`Computed field ${i + 1} key`}
                  value={row.key}
                  mono
                  placeholder="e.g. offer_price"
                  onCommit={(v) => update({ ...row, key: v.trim() })}
                />
                {badKey ? (
                  <span className="text-[10px] text-red-700">lower-case letters, digits or _ only</span>
                ) : null}
              </RowField>
              <RowField label="expression">
                <RowTextInput
                  ariaLabel={`Computed field ${i + 1} expression`}
                  value={row.expression}
                  mono
                  placeholder="round(prospect.custom_fields.list_price * 0.9, -2)"
                  onCommit={(v) => update({ ...row, expression: v })}
                />
              </RowField>
            </div>
          );
        }}
      />
    </Card>
  );
}
